// Angular
import { Injectable, OnDestroy } from '@angular/core';

// Reactive X
import { combineLatest, Observable, of, ReplaySubject, Subject, Subscriber } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take, takeUntil } from 'rxjs/operators';

// Internal dependencies
import { onComplete, onError, optimisticUpdate, retryUnless } from '../utils/operators';
import { Patch } from '../utils/types';
import { applyValuePatch, createJsonPatch } from '../utils/json-patch';

import { User } from '../../../app/providers/traccar-client';

import { TraccarClient } from './traccar/traccar-client';
import { TraccarSession } from './traccar/traccar-session';
import { AdminBackendClient } from './admin-backend/admin-backend-client';
import { AuthService } from './auth.service';
import { Invitation } from '../models/invitation/pending-invitation';

// Attributes that describe the device the app runs on rather than the customer. An impersonated
// session runs on a support phone or browser, so writing them would register that device on the
// customer: it would receive the customer's alarm pushes, and the customer's app metadata would
// show the support device. `fcmTokens` also covers the per-brand `fcmTokens<Brand>` attributes.
const DEVICE_ATTRIBUTE_PREFIXES = ['fcmTokens', 'locale', 'appVersion', 'appEnvironment', 'appPackage', 'sendAnalytics'];

function isTokenRemoval(key: string, value: unknown, currentUser: User): boolean {
  const current = currentUser?.attributes?.[key];
  return key.startsWith('fcmTokens') && Array.isArray(value) && Array.isArray(current)
    && value.every((token) => current.includes(token));
}

// Drops device attributes from a patch, except a token list that only removes tokens: stopping an
// impersonation removes the support device's token, which older app versions registered.
function withoutDeviceAttributes(userPatch: Patch<User>, currentUser: User): Patch<User> {
  const attributes = (userPatch as any)?.attributes;
  if (!attributes) return userPatch;

  const kept = Object.fromEntries(
    Object.entries(attributes).filter(([key, value]) =>
      !DEVICE_ATTRIBUTE_PREFIXES.some((prefix) => key.startsWith(prefix)) || isTokenRemoval(key, value, currentUser),
    ),
  );
  return { ...userPatch, attributes: kept } as Patch<User>;
}

type UserUpdate = {
  patch: Patch<User>;
  patchedUser: User;
  onSuccess?: () => void;
  onError?: (error: any) => void;
};

@Injectable({
  providedIn: 'root',
})
export class UserService implements OnDestroy {
  /* CONSTANTS */

  public static readonly DEFAULT_USER_TOKEN_LENGTH: number = 32;
  public static readonly DEFAULT_USER_TOKEN_SYMBOLS: string =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

  /* ATTRIBUTES */

  private imminentDestruction$: Subject<void> = new Subject();

  private userSource$: Subject<User> = new ReplaySubject(1);
  private userUpdates$: Subject<UserUpdate> = new Subject();

  public readonly user$: Observable<User> = this.userSource$.pipe(
    distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
    takeUntil(this.imminentDestruction$),
  );

  /* LIFECYCLE */

  public constructor(
    private traccar: TraccarClient,
    private traccarSession: TraccarSession,
    private adminBackend: AdminBackendClient,
    private auth: AuthService,
  ) {
    this.userUpdates$
      .pipe(
        optimisticUpdate(
          this.traccarSession.user$,
          (_, userUpdate: UserUpdate) => userUpdate.patchedUser,
          (currentUser, userUpdate: UserUpdate) => {
            const jsonPatch = createJsonPatch(currentUser, userUpdate.patch);
            return this.traccar.patch<User>(
              `users/${encodeURIComponent(currentUser.id)}`,
              jsonPatch,
              { headers: { 'Content-Type': 'application/json-patch+json' } },
            ).pipe(
              retryUnless(3, 1000, (error) => error?.status === 400),
              onError((error: any) => userUpdate.onError && userUpdate.onError(error)),
              onComplete(() => userUpdate.onSuccess && userUpdate.onSuccess()),
            );
          },
        ),
        takeUntil(this.imminentDestruction$),
      )
      .subscribe(this.userSource$);
  }

  public ngOnDestroy(): void {
    this.imminentDestruction$.next();
    this.imminentDestruction$.complete();

    this.userSource$.complete();
    this.userUpdates$.complete();
  }

  /* METHODS */

  private enqueueUserPatch(userPatch: Patch<User>, patchedUser: User): Observable<User> {
    return new Observable((subscriber: Subscriber<User>) => {
      const userUpdate: UserUpdate = {
        patch: userPatch,
        patchedUser,
        onSuccess: () => {
          subscriber.next(patchedUser);
          subscriber.complete();
        },
        onError: (error: any) => {
          subscriber.error(error);
        },
      };

      this.userUpdates$.next(userUpdate);
    });
  }

  public patchUser(requestedPatch: Patch<User>): Observable<User> {
    return combineLatest([this.user$, this.auth.user$]).pipe(
      take(1),
      switchMap(([currentUser, authUser]) => {
        const userPatch = authUser?.impersonatedBy
          ? withoutDeviceAttributes(requestedPatch, currentUser)
          : requestedPatch;
        const patchedUser = applyValuePatch<User>(currentUser, userPatch);

        // Check for changes
        if (createJsonPatch(currentUser, userPatch).length === 0) return of(currentUser);

        return this.enqueueUserPatch(userPatch, patchedUser);
      }),
    );
  }

  /**
   * NOTE: The returned user simply serves convenience (and compatibilty with existing code).
   * Its usage might indicate imperative programming. Whenever possible use the provided 'user$' stream instead.
   */
  public refreshUser(): Observable<User> {
    return this.traccarSession.update();
  }

  public refreshUserToken(tokenLength?: number, tokenSymbols?: string): Observable<User> {
    return this.user$.pipe(
      take(1),
      switchMap((user) => this.refreshTokenForUser(user, tokenLength, tokenSymbols)),
    );
  }

  public refreshUserTokenIfMissing(tokenLength?: number, tokenSymbols?: string): Observable<User> {
    return this.user$.pipe(
      take(1),
      filter((user) => user.token === null || user.token === undefined),
      switchMap((user) => this.refreshTokenForUser(user, tokenLength, tokenSymbols)),
    );
  }

  private refreshTokenForUser(
    user: User,
    tokenLength: number = UserService.DEFAULT_USER_TOKEN_LENGTH,
    tokenSymbols: string = UserService.DEFAULT_USER_TOKEN_SYMBOLS,
  ): Observable<User> {
    // Code as in Traccar Web but with proper randomisation instead of Math.random();
    const randomValues = new Uint32Array(tokenLength);
    window.crypto.getRandomValues(randomValues);

    let newToken: string = '';
    for (let i = 0; i < randomValues.length; i += 1) {
      newToken += tokenSymbols.charAt(randomValues[i] % tokenSymbols.length);
    }

    return this.patchUser({ token: newToken });
  }

  public getInvitations(): Observable<Invitation[]>{
    return this.adminBackend.get<any>('invitations/pending').pipe(
      map((response: Invitation[])=>{
        return response.map((invitation: any) => ({
          invitationExpiresAt: new Date(invitation.invitationExpiresAt),
          sharerEmail: invitation.sharerEmail,
        }));
      })

    );
  }
}
